// src/Pages/Dashboard/Inventory.jsx
import React, { useState, useEffect, useRef } from 'react';
import './Inventory.css';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../Services/firebase'; // --- REMOVED storage ---
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { uploadProductImage } from '../../Services/cloudinary'; // --- NEW: Cloudinary Service ---

import DeleteAuthModal from './DeleteAuthModal';

// --- Icon Components ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;

const currencies = {
    'GHS': '₵', 'NGN': '₦', 'USD': '$', 'GBP': '£', 'EUR': '€',
};

// --- NEW: Added viewMode prop ---
const Inventory = ({ viewMode = 'all' }) => {
    const { currentUser } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [uploading, setUploading] = useState(false);

    // --- State for Add/Edit Modal ---
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentProductId, setCurrentProductId] = useState(null);
    const [productName, setProductName] = useState('');
    const [productType, setProductType] = useState('Physical');
    const [price, setPrice] = useState('');
    const [currency, setCurrency] = useState('GHS'); 
    const [quantity, setQuantity] = useState('');
    const [description, setDescription] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const fileInputRef = useRef(null);
    
    // --- Channel Toggles ---
    const [sellInStore, setSellInStore] = useState(true);
    const [sellOnline, setSellOnline] = useState(true);

    const [itemToDelete, setItemToDelete] = useState(null);

    // --- NEW: Auto-open modal if sidebar "Add Product" is clicked ---
    useEffect(() => {
        if (viewMode === 'add-new') {
            openAddModal();
        }
    }, [viewMode]);

    useEffect(() => {
        if (!currentUser) return;
        const productsCollectionRef = collection(db, 'businesses', currentUser.uid, 'products');
        const unsubscribe = onSnapshot(productsCollectionRef, (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(productsData);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching products:", err);
            setError('Failed to load products.');
            setLoading(false);
        });
        return () => unsubscribe();
    }, [currentUser]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!currentUser) return setError("You are not logged in.");

        if (!sellInStore && !sellOnline) {
            return setError("Product must be available in at least one sales channel.");
        }

        if (imageFile && imageFile.size > 5 * 1024 * 1024) { 
            return setError("Image file is too large. Please upload an image under 5MB.");
        }
        
        setUploading(true);
        let imageUrl = existingImageUrl;

        if (imageFile) {
            try {
                // --- NEW: Cloudinary Upload Magic ---
                imageUrl = await uploadProductImage(imageFile, currentUser.uid);
            } catch (err) {
                console.error("Image upload error:", err);
                setError("Failed to upload image. Please check your connection.");
                setUploading(false);
                return;
            }
        }
        
        const productData = {
            name: productName,
            type: productType,
            price: Number(price),
            currency: currency, 
            description: description,
            imageUrl: imageUrl || '',
            sellInStore: sellInStore,
            sellOnline: sellOnline,  
        };
        
        if (productType === 'Physical') {
            productData.quantity = Number(quantity);
        }

        try {
            if (isEditMode) {
                const productDocRef = doc(db, 'businesses', currentUser.uid, 'products', currentProductId);
                await updateDoc(productDocRef, productData);
            } else {
                productData.createdAt = new Date();
                const productsCollectionRef = collection(db, 'businesses', currentUser.uid, 'products');
                await addDoc(productsCollectionRef, productData);
            }
            closeModalAndResetForm();
        } catch (err) {
            console.error("Firestore error:", err);
            setError("Failed to save product. Please try again.");
        } finally {
            setUploading(false);
        }
    };
    
    const executeDelete = async () => {
        if (!currentUser || !itemToDelete) return;
        try {
            // Delete product reference from Firestore
            await deleteDoc(doc(db, 'businesses', currentUser.uid, 'products', itemToDelete.id));
            
            // Note: Since this is a frontend-only app using Unsigned Uploads, 
            // images remain safely stored on Cloudinary. If you ever build a Node.js backend, 
            // you can trigger a hard-delete on Cloudinary from there.
        } catch (err) {
            console.error("Delete error:", err);
        } finally {
            setItemToDelete(null);
        }
    };

    const openAddModal = () => {
        setIsEditMode(false);
        resetFormFields();
        setIsModalOpen(true);
    };

    const openEditModal = (product) => {
        setIsEditMode(true);
        setCurrentProductId(product.id);
        setProductName(product.name);
        setProductType(product.type);
        setPrice(product.price);
        setCurrency(product.currency || 'GHS'); 
        setQuantity(product.quantity || '');
        setDescription(product.description || '');
        setExistingImageUrl(product.imageUrl || '');
        
        setSellInStore(product.sellInStore !== false);
        setSellOnline(product.sellOnline !== false);

        setIsModalOpen(true);
    };

    const resetFormFields = () => {
        setProductName('');
        setProductType('Physical');
        setPrice('');
        setCurrency('GHS'); 
        setQuantity('');
        setDescription('');
        setExistingImageUrl('');
        
        // If they opened the modal from "Online Products", default it to online only
        if (viewMode === 'online') {
            setSellInStore(false);
            setSellOnline(true);
        } else {
            setSellInStore(true); 
            setSellOnline(true);  
        }
        
        setImageFile(null);
        if(fileInputRef.current) fileInputRef.current.value = null;
    };
    
    const closeModalAndResetForm = () => {
        setIsModalOpen(false);
        resetFormFields();
        setError('');
    };

    // --- NEW: Filter products based on Sidebar View Mode ---
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // If viewMode is 'online', strictly hide POS-only items
        if (viewMode === 'online' && product.sellOnline === false) return false;
        
        return matchesSearch;
    });

    return (
        <div className="inventory-container">
            <div className="inventory-header">
                <h2>
                    {viewMode === 'online' ? 'Online Products' : 'Your Inventory'}
                </h2>
                <div className="header-actions">
                     <div className="search-wrapper">
                        <SearchIcon />
                        <input 
                            type="text" 
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="add-product-btn" onClick={openAddModal}>
                        <PlusIcon />
                        <span>Add New Product</span>
                    </button>
                </div>
            </div>
            
            {loading ? <p>Loading products...</p> : filteredProducts.length === 0 ? (
                <div className="no-products-view">
                    <h3>{searchTerm ? 'No products match your search.' : 'Your inventory is empty!'}</h3>
                    <p>{!searchTerm && 'Click "Add New Product" to get started.'}</p>
                </div>
            ) : (
                <div className="products-table-container">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Product Name</th>
                                <th>Type</th>
                                <th>Price</th>
                                <th>Channels</th> 
                                <th>Stock</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(product => (
                                <tr key={product.id}>
                                    <td>
                                        <div className="product-image-thumbnail" style={{backgroundImage: `url(${product.imageUrl || 'https://placehold.co/60x60/f9fafb/e5e7eb?text=Img'})`}}></div>
                                    </td>
                                    <td>{product.name}</td>
                                    <td><span className={`product-type-badge ${product.type.toLowerCase()}`}>{product.type}</span></td>
                                    <td>{currencies[product.currency] || '₵'}{product.price.toFixed(2)}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {product.sellInStore !== false && <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: '4px' }}>POS</span>}
                                            {product.sellOnline !== false && <span style={{ background: '#dcfce3', color: '#166534', padding: '2px 6px', borderRadius: '4px' }}>Web</span>}
                                        </div>
                                    </td>
                                    <td>{product.type === 'Physical' ? product.quantity : 'N/A'}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button className="action-btn edit" onClick={() => openEditModal(product)}><EditIcon/></button>
                                            <button className="action-btn delete" onClick={() => setItemToDelete(product)}><TrashIcon/></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DeleteAuthModal 
                isOpen={itemToDelete !== null}
                onClose={() => setItemToDelete(null)}
                itemName={itemToDelete?.name || 'Product'}
                onSuccess={executeDelete}
            />

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-modal-btn" onClick={closeModalAndResetForm}><XIcon /></button>
                        <h3>{isEditMode ? 'Edit Product' : 'Add a New Product'}</h3>
                        <form onSubmit={handleFormSubmit}>
                            <div className="form-group">
                                <label>Product Image (Optional, max 5MB)</label>
                                <input type="file" accept="image/*" ref={fileInputRef} onChange={e => setImageFile(e.target.files[0])} />
                            </div>
                            <div className="form-group"><label>Product Name</label><input type="text" value={productName} onChange={e => setProductName(e.target.value)} required /></div>
                            <div className="form-group"><label>Product Type</label><select value={productType} onChange={e => setProductType(e.target.value)}><option value="Physical">Physical Good</option><option value="Digital">Digital Product</option><option value="Ticket">Event Ticket</option></select></div>
                            
                            <div className="form-group">
                                <label>Price</label>
                                <div className="price-group">
                                    <select className="currency-input" value={currency} onChange={e => setCurrency(e.target.value)}>
                                        {Object.keys(currencies).map(code => (
                                            <option key={code} value={code}>{code}</option>
                                        ))}
                                    </select>
                                    <input className="price-input" type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} required />
                                </div>
                            </div>

                            {productType === 'Physical' && (<div className="form-group"><label>Quantity in Stock</label><input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} required /></div>)}
                            
                            <div className="form-group">
                                <label>Sales Channels</label>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={sellInStore} onChange={e => setSellInStore(e.target.checked)} />
                                        Point of Sale (In-Store)
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={sellOnline} onChange={e => setSellOnline(e.target.checked)} />
                                        Online Storefront
                                    </label>
                                </div>
                            </div>

                            <div className="form-group"><label>Description (Optional)</label><textarea value={description} onChange={e => setDescription(e.target.value)}></textarea></div>
                            {error && <p className="modal-error">{error}</p>}
                            <button type="submit" className="modal-submit-btn" disabled={uploading}>{uploading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Add Product')}</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;
